import { useParams } from "react-router-dom";
import { useEmissionsSourceById } from "../api/emissions-sources";
import MainLayout from "../components/MainLayout";
import ContentContainer from "../components/ContentContainer";
import { ArrowLeft, Globe, WarningCircle } from "@phosphor-icons/react";
import Typography from "../components/Typography";
import SectionTitle from "../components/SectionTitle";
import Button from "../components/Button";
import { RequireEmissionsSourceEvidenceTable } from "../components/RequiredEvidenceTable";
import { appPaths } from "../Routes";
import IconButton from "../components/IconButton";
import { dataStatusOptions } from "../constants";
import clsx from "clsx";
import ActionMenu from "../components/ActionMenu";
import DialogBox, { useDialogBoxState } from "../components/DialogBox";
import { ActionMenuItemOption } from "../components/ActionMenu/ActionMenuOption";
import DataSourceCalculationCards from "../components/DataSourceCalculationFactorCards";
import Chip from "../components/Chip";
import Avatar from "../components/Avatar";
import { format } from "date-fns";
import {
    useAddDataSource,
    useDataSourceById,
    useSetDataSourceCalculation,
} from "../api/data-sources";
import { getInitialsFromDisplayName } from "../utils/getInitialsFromDisplayNames";
import SetCalculationForm from "../components/SetCalculationForm";
import { SetCalculationFormValues } from "../components/SetCalculationForm/SetCalculationFormValues";
import { errorToast, successToast } from "../toast";
import { formatNumber } from "../utils/formatNumber";
import AddDataSourceForm from "../components/AddDataSourceForm";
import { AddDataSourceDto } from "../api/models/DataSource";
import {
    AddDataSourceFormValues,
} from "../components/AddDataSourceForm/AddDataSourceFormValues";

const ConnectedDataSourceDetail = ({
    id,
    dataStatusId,
}: {
    id: number;
    dataStatusId: number;
}) => {
    const { dataSource, refetch } = useDataSourceById(id);
    const [setCalculation, isSettingCalculation] = useSetDataSourceCalculation(
        Number(id)
    );

    const handleSetCalculationFormSubmission = async (
        value: SetCalculationFormValues
    ) => {
        if (value.activeFrom === null) return;

        const response = await setCalculation({
            ...value,
            activeFrom: format(value.activeFrom, "yyyy-MM-dd"),
        });

        if (!response.success) {
            errorToast(
                "There was a problem creating the emissions factor - please try again"
            );
            return;
        }

        refetch();
        successToast("Emissions factor set");
        setCalculationDialogState.close();
    };

    const setCalculationDialogState = useDialogBoxState();

    if (!dataSource) return <></>;

    const dataCollectionType = dataStatusOptions.find(
        (option) => option.value === dataStatusId
    )?.label;

    const actions: ActionMenuItemOption[] = [];

    if (dataSource.currentCalculationSetAt) {
        actions.push({
            label: "Set emissions factor",
            onClick: setCalculationDialogState.open,
        });
    }

    if (
        dataSource.currentCalculationSetAt ||
        dataSource.calculationHistoryCount !== 0
    ) {
        actions.push({
            label: "View history",
            to: appPaths.dataSourceEmissionsFactorHistory(id.toString()),
        });
    }

    actions.push({
        label: dataStatusId < 3 ? "Report emissions" : "Add activity data",
        to: appPaths.dataSourceAddActivity(id.toString()),
        disabled: !!dataSource.activityMetadataTypeId,
    });

    return (
        <>
            <SectionTitle title="Emissions Calculation">
                <>
                    {dataSource.currentCalculationActiveFrom ? (
                        <Chip colour="green" className="mr-auto ml-3">
                            Applicable from{" "}
                            {dataSource.currentCalculationActiveFrom}
                        </Chip>
                    ) : (
                        <></>
                    )}
                    <div className="flex gap-6 ml-auto">
                        <ActionMenu label="Actions" options={actions} />
                    </div>
                </>
            </SectionTitle>
            {dataSource.currentCalculationSetAt ? (
                <>
                    <div className="flex items-center sm:space-x-4">
                        <div className="hidden sm:block">
                            <Avatar
                                initials={getInitialsFromDisplayName(
                                    dataSource.currentCalculationSetByDisplayName
                                )}
                            />
                        </div>
                        <div>
                            <Typography
                                variant="body-large"
                                component="p"
                                className="w-full max-w-full overflow-hidden overflow-ellipsis"
                            >
                                <b>Submitted by:</b>{" "}
                                {dataSource.currentCalculationSetByDisplayName ??
                                    "--"}
                            </Typography>
                            <div className="flex">
                                <Typography
                                    variant="body-large"
                                    component="p"
                                    className="overflow-hidden overflow-ellipsis mr-4"
                                >
                                    <b>Submitted on date:</b>{" "}
                                    {dataSource.currentCalculationSetAt
                                        ? format(
                                              new Date(
                                                  dataSource.currentCalculationSetAt
                                              ),
                                              "dd/MM/yyyy"
                                          )
                                        : "--"}
                                </Typography>
                            </div>
                        </div>
                    </div>
                    <div className="bg-surface-light-1 rounded-xl p-6  mt-8">
                        <div className="grid grid-cols-2 gap-3 space-y-0 md-lg:grid-cols-3 justify-between">
                            <span>
                                <Typography
                                    variant="title-small"
                                    component="h2"
                                >
                                    Data Collection Type:{" "}
                                </Typography>
                                {dataCollectionType ?? "--"}
                            </span>
                            <span>
                                <Typography
                                    variant="title-small"
                                    component="h2"
                                >
                                    Material:{" "}
                                </Typography>
                                {dataSource.rawMaterialName ?? "--"}
                            </span>
                            <span>
                                <Typography
                                    variant="title-small"
                                    component="h2"
                                >
                                    Raw unit:{" "}
                                </Typography>
                                {dataSource.rawUnitsSuffixHtml ?? "--"}
                            </span>
                        </div>
                        {dataStatusId > 2 && (
                            <DataSourceCalculationCards {...dataSource} />
                        )}
                    </div>
                </>
            ) : (
                <div className="flex justify-center bg-surface-light-1 rounded-xl p-6">
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 text-surface-dark">
                            <div>No emissions factor set</div>
                            <WarningCircle size={20} />
                        </div>
                        <Button
                            variant="outline"
                            onClick={setCalculationDialogState.open}
                            className="mx-auto"
                        >
                            Set emissions factor
                        </Button>
                    </div>
                </div>
            )}
            <DialogBox
                title="New emissions factor"
                {...setCalculationDialogState}
            >
                <SetCalculationForm
                    locationName={dataSource.locationName}
                    dataSourceName={dataSource.name}
                    isLoading={isSettingCalculation}
                    isOpen={setCalculationDialogState.isOpen}
                    onCancel={setCalculationDialogState.close}
                    onSubmit={handleSetCalculationFormSubmission}
                />
            </DialogBox>
        </>
    );
};

const EmissionsSourceDetailPage = () => {
    const { id } = useParams();
    const setEmissionsCalculationInput = useDialogBoxState();

    const { emissionsSource, isLoading, refetch } = useEmissionsSourceById(
        Number(id)
    );

    const [request, isAddingDataSource] = useAddDataSource();

    const initialValues: Partial<AddDataSourceFormValues> = {
        emissionsSourceId: Number(id),
        connectionTypeId: 1,
        activityMetadataTypeId: 0,
    };

    const handleSetEmissionsCalculationInputs = async (
        values: AddDataSourceFormValues
    ) => {
        const dto: AddDataSourceDto = {
            ...values,
        };

        const response = await request(dto);

        if (!response.success) {
            errorToast(
                "There was a problem setting calculation inputs - please try again"
            );
            return;
        }

        successToast("Calculation inputs set");
        setEmissionsCalculationInput.close();
        refetch();
    };

    const buildActionMenuOptions = (
        id: number,
        dataSourceId: number | undefined
    ) => {
        const actionMenuOptions: ActionMenuItemOption[] = [
            {
                label: "Edit asset",
                to: appPaths.emissionsSourceEdit(id.toString()),
            },
        ];

        return actionMenuOptions;
    };

    const setCalculationForms = async () => {
        return setEmissionsCalculationInput.open();
    };

    return (
        <MainLayout isLoading={isLoading}>
            {!emissionsSource && (
                <ContentContainer className="py-10 text-center">
                    <p>No asset found matching this id</p>
                </ContentContainer>
            )}
            {emissionsSource && (
                <ContentContainer className="pt-14 pb-11">
                    <div className="flex sm:flex-row gap-6 items-center pb-5">
                        <div className="flex items-center">
                            <IconButton
                                label="Back"
                                icon={ArrowLeft}
                                iconSize="large"
                                className="mr-3"
                                onClick={() => window.history.back()}
                            />
                            <Typography variant="headline-small" component="h1">
                                {emissionsSource.name}
                            </Typography>
                        </div>
                        <div className="ml-auto flex items-center gap-2 font-semibold min-w-fit">
                            <Globe weight="light" size={24} />
                            <Typography
                                variant="headline-small"
                                component="span"
                                className="text-deep-ocean-20"
                            >
                                Scope {emissionsSource.scope}
                            </Typography>
                        </div>
                    </div>
                    <SectionTitle title="Profile" />
                    <div className="flex justify-between items-center">
                        <span>
                            <span
                                className={clsx(
                                    "text-gray text-center rounded-md text-xs mx-1 py-1 px-3",
                                    "bg-gray-100"
                                )}
                            >
                                {emissionsSource.isMaterial === true
                                    ? "Material"
                                    : "Not Material"}
                            </span>
                        </span>
                        <ActionMenu
                            label="Actions"
                            options={buildActionMenuOptions(
                                emissionsSource.id,
                                emissionsSource.dataSourceId
                            )}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3 space-y-0 md-lg:grid-cols-3 justify-between bg-surface-light-1 rounded-xl my-5 p-6">
                        <span>
                            <Typography variant="title-small" component="h2">
                                Unique Reference:
                            </Typography>
                            {emissionsSource.uniqueReference}
                        </span>
                        <span>
                            <Typography variant="title-small" component="h2">
                                Enterprise Value (£):
                            </Typography>
                            {formatNumber(emissionsSource.enterpriseValue)}
                        </span>
                        <span>
                            <Typography variant="title-small" component="h2">
                                Outstanding Amount (£):
                            </Typography>
                            {formatNumber(emissionsSource.outstandingAmount)}
                        </span>
                        <span>
                            <Typography variant="title-small" component="h2">
                                Location:
                            </Typography>
                            {emissionsSource.locationName}
                        </span>
                        <span>
                            <Typography variant="title-small" component="h2">
                                Portfolio:
                            </Typography>
                            {emissionsSource.portfolioName}
                        </span>
                        <span>
                            <Typography variant="title-small" component="h2">
                                Sector:
                            </Typography>
                            {emissionsSource.sectorName}
                        </span>
                    </div>
                    {!emissionsSource.dataSourceId && (
                        <>
                            <SectionTitle title="Emissions Calculation" />
                            <div className="bg-surface-light-1 rounded-xl p-6">
                                <div className="flex items-center justify-center gap-2 text-surface-dark">
                                    <div>No calculation found</div>
                                    <WarningCircle size={20} />
                                </div>
                                <div className="flex items-center justify-center gap-2 my-3">
                                    <Button onClick={setCalculationForms}>
                                        Set calculation inputs
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                    {emissionsSource.dataSourceId && (
                        <ConnectedDataSourceDetail
                            id={emissionsSource.dataSourceId}
                            dataStatusId={emissionsSource.dataStatusId}
                        />
                    )}

                    <RequireEmissionsSourceEvidenceTable
                        title="Evidence"
                        defaultStatusFilter="All"
                        emissionsSourceId={emissionsSource.id}
                        noEvidenceMessage="No evidence found"
                        hideLinkToEmissionsSource={true}
                    />
                    <DialogBox
                        title="Set Emissions Calculation Inputs"
                        {...setEmissionsCalculationInput}
                    >
                        <AddDataSourceForm
                            initialValues={initialValues}
                            isSubmitting={isAddingDataSource}
                            onCancel={setEmissionsCalculationInput.close}
                            onSubmit={handleSetEmissionsCalculationInputs}
                        />
                    </DialogBox>
                </ContentContainer>
            )}
        </MainLayout>
    );
};

export default EmissionsSourceDetailPage;
